import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { cn } from "../../lib/utils";
import { SummaryTiles, Hint, PenaltyPanel, penaltyRowsFromPayments, downloadClientDocument } from "./PaymentBits";
import { ServiceRow, groupServices } from "./ProjectPayments";
import ProjectPicker from "./ProjectPicker";
import { moneyList, sumByCurrency, days, fmtDate } from "./format";
import { useClientProject, resolveScope } from "./projectScope";
import { tr } from "./i18n";

const STATUS = [
  // Filters work on PAYMENTS (parts), not services: a 50/50 service fully paid
  // is "Оплачено 2", and a service shows in every tab one of its parts matches.
  { key: "due", label: "До оплати", test: (x) => x.state === "overdue" || x.state === "invoiced" },
  { key: "planned", label: "Заплановано", test: (x) => x.state === "not_invoiced" },
  { key: "paid", label: "Оплачено", test: (x) => x.state === "paid" },
  { key: "all", label: "Усі", test: () => true },
];

/**
 * Client's payments page — the same service/parts view as the project
 * (advance vs final, dates, penalty, payer/payee, invoice/act), grouped by
 * project, with a project filter and a status filter.
 */
const ClientPaymentsView = () => {
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(true);
  // Same project scope as the dashboard (shared store); ?project= deep-links.
  const [scope, setProjectId] = useClientProject();
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("project");
    if (q) setProjectId(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [status, setStatus] = useState("due");

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get("/client/portal");
      setPortal(res.data);
    } catch (e) {
      toast.error(tr("Не вдалося завантажити рахунки"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading && !portal) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  }
  const projects = portal?.projects || [];
  const projectId = resolveScope(scope, projects);
  const allPayments = portal?.payments || [];
  const rules = Object.fromEntries(projects.map((p) => [p.id, p.penalty_rule]));
  const scoped = projectId === "all" ? allPayments : allPayments.filter((p) => p.project_id === projectId);
  const selectedProject = projects.find((p) => p.id === projectId) || null;

  const overdue = sumByCurrency(scoped.filter((p) => p.state === "overdue"));
  const due = sumByCurrency(scoped.filter((p) => p.state === "invoiced"));
  const planned = sumByCurrency(scoped.filter((p) => p.state === "not_invoiced"));
  const paid = sumByCurrency(scoped.filter((p) => p.state === "paid"));
  const nextDue = scoped.filter((p) => p.state === "invoiced" && p.invoice_due_date).map((p) => p.invoice_due_date).sort()[0];

  const statusDef = STATUS.find((s) => s.key === status);
  // Grouped by project, services inside; empty projects dropped.
  const sections = (projectId === "all" ? projects : projects.filter((p) => p.id === projectId))
    .map((pr) => ({
      project: pr,
      services: groupServices(allPayments.filter((x) => x.project_id === pr.id)).filter((g) => g.parts.some(statusDef.test)),
    }))
    .filter((s) => s.services.length > 0)
    .sort((a, b) => Math.min(...a.services.map((g) => g.urgency)) - Math.min(...b.services.map((g) => g.urgency)));
  const countFor = (key) => scoped.filter(STATUS.find((s) => s.key === key).test).length;
  const anyRule = (selectedProject?.penalty_rule?.enabled && selectedProject.penalty_rule) ||
    Object.values(rules).find((r) => r?.enabled) || null;
  const grace = anyRule?.grace_period_days || 0;
  const howItWorks = [
    tr("Кожна послуга може бути розділена на частини: передоплата запускає роботу, фінальний платіж — після здачі."),
    tr("Оплата — переказом за реквізитами з рахунку за договором; у рядку видно, хто платник і хто отримувач."),
    anyRule ? `${tr("Якщо оплата затримується")}${grace ? ` ${tr("більше ніж на")} ${days(grace)}` : ""} ${tr("після дати в рахунку — пеня")} ${anyRule.rate_per_day}% ${tr("за день.")}` : null,
    tr("Оплата карткою — лише за посиланням від менеджера."),
  ].filter(Boolean).join(" ");

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 font-semibold">{tr("Оплата")}</p>
          <h1 className="text-2xl font-extrabold text-zinc-50 font-['Manrope'] tracking-tight mt-1 flex items-center gap-2">
            {tr("Рахунки та оплата")} <Hint text={howItWorks} />
          </h1>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-2 h-8 rounded-full">
          <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} /> {tr("Оновити")}
        </Button>
      </div>

      {projects.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <ProjectPicker
            projects={[{ id: "all", name: tr("Усі проєкти"), status: null }, ...projects]}
            selected={projectId === "all" ? { id: "all", name: tr("Усі проєкти") } : selectedProject}
            onSelect={setProjectId}
            flagOf={(pr) => {
              if (pr.id === "all") return null;
              const pp = allPayments.filter((x) => x.project_id === pr.id);
              if (pp.some((x) => x.state === "overdue")) return "bg-red-500";
              if (pp.some((x) => x.state === "invoiced")) return "bg-amber-500";
              return null;
            }}
          />
        </div>
      )}

      <SummaryTiles
        tiles={[
          { label: tr("Прострочено"), value: moneyList(overdue) || tr("Нічого"), tone: overdue.length ? "overdue" : "muted" },
          { label: tr("Оплатити в строк"), value: moneyList(due) || tr("Нічого"), tone: due.length ? "due" : "muted", hint: nextDue ? `${tr("найближчий до")} ${fmtDate(nextDue)}` : null },
          { label: tr("Ще не виставлено"), value: moneyList(planned) || "—", tone: "muted", hint: planned.length ? tr("платити поки не потрібно") : null },
          { label: tr("Оплачено"), value: moneyList(paid) || "—", tone: paid.length ? "paid" : "muted" },
        ]}
      />

      <PenaltyPanel rows={penaltyRowsFromPayments(scoped, rules)} rule={anyRule} />

      <div className="flex flex-wrap gap-1 rounded-xl border border-zinc-800 bg-zinc-900/60 p-1 w-fit">
        {STATUS.map((s) => {
          const n = countFor(s.key);
          return (
            <button key={s.key} type="button" onClick={() => setStatus(s.key)}
              className={cn("inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition-colors",
                status === s.key ? "bg-zinc-800 text-zinc-50 font-semibold" : "text-zinc-400 hover:text-zinc-100")}>
              {tr(s.label)}
              <span className="text-[11px] text-zinc-500">{n}</span>
            </button>
          );
        })}
      </div>

      {sections.length === 0 ? (
        <Card className="bg-zinc-900/60 border-zinc-800 rounded-2xl">
          <CardContent className="py-10 text-center text-sm text-zinc-400">
            {status === "due" ? tr("Зараз нічого оплачувати не потрібно") : tr("Тут поки порожньо")}
          </CardContent>
        </Card>
      ) : sections.map(({ project, services }) => (
        <section key={project.id} className="space-y-2">
          {projectId === "all" && (
            <p className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-semibold pt-1">{project.name}</p>
          )}
          <div className="space-y-2">
            {services.map((g) => (
              <ServiceRow key={g.key} parts={g.parts} penaltyRule={project.penalty_rule}
                onDownload={(id, type) => downloadClientDocument(id, type, "uk")} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default ClientPaymentsView;
